import { useEffect, useState } from 'react';
import type { CreditRiskType } from '../types/CreditRiskType';
import { getCreditRiskTypes } from '../api/creditRiskTypes';

// Module-level cache (see useAssetTypes for the canonical pattern). Fetched
// lazily when first used (AssetsPage / AssetRecordPage dropdowns); cleared on
// logout via clearCreditRiskTypesCache().
let cache: CreditRiskType[] | null = null;
let inflight: Promise<CreditRiskType[]> | null = null;

function loadCreditRiskTypes(): Promise<CreditRiskType[]> {
  if (cache) return Promise.resolve(cache);
  if (inflight) return inflight;
  inflight = getCreditRiskTypes()
    .then((data) => {
      cache = data;
      inflight = null;
      return data;
    })
    .catch((err: unknown) => {
      inflight = null;
      throw err;
    });
  return inflight;
}

export function clearCreditRiskTypesCache(): void {
  cache = null;
  inflight = null;
}

interface UseCreditRiskTypesResult {
  data: CreditRiskType[] | null;
  loading: boolean;
  error: string | null;
}

export function useCreditRiskTypes(): UseCreditRiskTypesResult {
  const [state, setState] = useState<UseCreditRiskTypesResult>(() =>
    cache
      ? { data: cache, loading: false, error: null }
      : { data: null, loading: true, error: null },
  );

  useEffect(() => {
    if (cache) {
      setState((prev) =>
        prev.data === cache
          ? prev
          : { data: cache, loading: false, error: null },
      );
      return;
    }

    let cancelled = false;
    loadCreditRiskTypes()
      .then((data) => {
        if (cancelled) return;
        setState({ data, loading: false, error: null });
      })
      .catch((err: unknown) => {
        if (cancelled) return;
        const message = err instanceof Error ? err.message : String(err);
        setState({ data: null, loading: false, error: message });
      });

    return () => {
      cancelled = true;
    };
  }, []);

  return state;
}
